import React, { useState } from 'react';
import { FaUser, FaArrowLeft, FaSignOutAlt, FaCreditCard, FaCoins, FaChartLine, FaGift } from 'react-icons/fa';
import { ClipboardList, Receipt, HeadsetIcon, KeyRound, Building2, FileText, ChevronRight, UserCircle, ShieldCheck, Settings, Award, TrendingUp, Lock, CreditCard, ArrowUpRight } from 'lucide-react';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import { Link } from 'react-router-dom';
import DepositRecord from './DepositRecord';
import CashoutView from './CashoutView';
import WithdrawalRecord from './WithdrawalRecord';
import ChangePassword from './ChangePassword';
import WhatsAppFloat from './auth/WhatsAppFloat';

const Me = () => {
  const [currentView, setCurrentView] = useState('main');
  const { userDetails, token } = useAuth();
  const authContext = useAuth();
  const [toastNotification, setToastNotification] = useState(null);
  
  const features = [
    { name: "Transaction History", icon: <ClipboardList size={20} />, view: 'deposit', description: "View your deposit history" },
    { name: "Payout Records", icon: <Receipt size={20} />, view: 'withdrawal', description: "Check your withdrawal transactions" },
    { name: "Support Center", icon: <HeadsetIcon size={20} />, view: 'customer', description: "Get help from our team" },
    { name: "Security Settings", icon: <KeyRound size={20} />, view: 'password', description: "Manage your account security" },
    { name: "Company Profile", icon: <Building2 size={20} />, view: 'about', description: "Learn about VisionTech2030" },
    { name: "Legal Terms", icon: <FileText size={20} />, view: 'terms', description: "Read our terms of service" }
  ];

  function logout() {
    authContext.logout();
  }

  const joinTelegramChannel = () => {
    const telegramLink = "https://t.me/+y-cgsiLhbKBiYjk0";
    window.open(telegramLink, '_blank');
  };

  const whatsappservice = () => {
    const whatsappLink = "https://wa.link/8u24qf";
    window.open(whatsappLink, '_blank');
  };

  const calculateTotalBalance = () => {
    const wallet = Number(userDetails.wallet) || 0;
    const invitationIncome = Number(userDetails.invitationIncome) || 0;
    return (wallet + invitationIncome).toLocaleString();
  };

  const renderMainView = () => (
    <div className="vt-profile-main-view">
      <div className="vt-profile-background-decoration">
        <div className="vt-profile-bg-circle vt-profile-bg-circle-1"></div>
        <div className="vt-profile-bg-circle vt-profile-bg-circle-2"></div>
      </div>
      
      <div className="vt-profile-user-container">
        <div className="vt-profile-user-header">
          <div className="vt-profile-avatar-wrapper">
            <div className="vt-profile-avatar-border">
              <div className="vt-profile-avatar">
                <UserCircle size={50} strokeWidth={1.5} className="vt-profile-avatar-icon" />
              </div>
            </div>
            <div className="vt-profile-verified-badge">
              <ShieldCheck size={14} />
            </div>
          </div>
          <div className="vt-profile-user-info">
            <h2 className="vt-profile-name">{userDetails.fullName}</h2>
            <p className="vt-profile-mobile">{userDetails.mobile}</p>
          </div>
        </div>
      </div>

      <div className="vt-profile-financial-summary">
        <div className="vt-profile-summary-card">
          <div className="vt-profile-summary-card-inner">
            <div className="vt-profile-summary-header">
              <h3 className="vt-profile-summary-title">Total Balance</h3>
            </div>
            <div className="vt-profile-balance-amount">
              <span className="vt-profile-currency">KES</span>
              <span className="vt-profile-amount">{calculateTotalBalance()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="vt-profile-menu-container">
        <h3 className="vt-profile-section-title">
          <Settings size={16} className="vt-profile-section-icon" />
          <span>Account & Settings</span>
        </h3>
        
        <div className="vt-profile-menu">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="vt-profile-menu-item" 
              onClick={() => setCurrentView(feature.view)}
            >
              <div className="vt-profile-menu-icon-wrapper">
                <div className="vt-profile-menu-icon">
                  {feature.icon}
                </div>
              </div>
              <div className="vt-profile-menu-content">
                <div className="vt-profile-menu-info">
                  <span className="vt-profile-menu-title">{feature.name}</span>
                  <span className="vt-profile-menu-description">{feature.description}</span>
                </div>
                <ChevronRight size={18} className="vt-profile-menu-arrow" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Link to={"/login"} className="vt-profile-logout-link">
        <button className="vt-profile-logout-button" onClick={logout}>
          <FaSignOutAlt className="vt-profile-logout-icon" />
          <span>Sign Out</span>
        </button>
      </Link>
      
      <div className="vt-profile-footer">
        <div className="vt-profile-footer-logo">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" 
              stroke="currentColor" fill="none" strokeWidth="2" 
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>VisionTech2030</span>
        </div>
        <p className="vt-profile-footer-copyright">
          © {new Date().getFullYear()} All rights reserved
        </p>
        <p className="vt-profile-footer-version">Version 2.1.0</p>
      </div>
    </div>
  );

  const renderDepositRecord = () => (
    <div className="vt-profile-view-container">
      <DepositRecord userDetails={userDetails} token={authContext.token} />
    </div>
  );

  const renderWithdrawalRecord = () => (
    <div className="vt-profile-view-container">
      <WithdrawalRecord userDetails={userDetails} token={token} />
    </div>
  );

  const renderCustomerService = () => (
    <div className="vt-profile-view-container">
      <div className="vt-profile-content-card">
        <div className="vt-profile-view-header">
          <h2 className="vt-profile-view-title">Customer Support</h2>
          <div className="vt-profile-view-subtitle">
            <HeadsetIcon size={16} className="vt-profile-subtitle-icon" />
            <span>24/7 Assistance</span>
          </div>
        </div>
        
        <p className="vt-profile-view-description">
          Need help? Our support team is ready to assist you with any questions or concerns you might have.
        </p>
        
        <div className="vt-profile-support-channels">
          <button className="vt-profile-support-channel telegram" onClick={joinTelegramChannel}>
            <svg className="vt-profile-channel-icon" viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.95 8.03c-.2 2.06-.9 7.07-1.28 9.4-.16.95-.47 1.27-.77 1.3-.66.07-1.17-.43-1.82-.85-.98-.65-1.58-1.03-2.5-1.65-1.05-.71-.36-1.11.24-1.76.16-.17 2.78-2.54 2.83-2.76.01-.04.01-.19-.07-.27-.08-.08-.22-.05-.31-.03-.13.03-2.24 1.42-6.33 4.17-.6.41-1.14.61-1.62.6-.53-.02-1.55-.3-2.3-.55-.93-.31-1.67-.47-1.6-1 .03-.26.35-.52.95-.78 3.75-1.73 6.26-2.86 7.52-3.4 3.58-1.53 4.33-1.8 4.81-1.8.11 0 .35.03.51.17.12.1.15.25.17.35.02.15.04.34.02.56z" fill="currentColor"/>
            </svg>
            <div className="vt-profile-channel-content">
              <span className="vt-profile-channel-name">Telegram Channel</span>
              <span className="vt-profile-channel-desc">Join our community</span>
            </div>
          </button>
          
          <button className="vt-profile-support-channel whatsapp" onClick={whatsappservice}>
            <svg className="vt-profile-channel-icon" viewBox="0 0 24 24" width="24" height="24">
              <path d="M17.6 6.2c-1.5-1.5-3.4-2.3-5.5-2.3-4.3 0-7.8 3.5-7.8 7.8 0 1.4.4 2.8 1.1 4l-1.2 4.1 4.2-1.1c1.2.7 2.5 1 3.8 1 4.3 0 7.8-3.5 7.8-7.8-.1-2.1-.9-4-2.4-5.7zM12.1 18.9c-1.2 0-2.3-.3-3.3-.9l-.2-.1-2.4.6.6-2.4-.2-.2c-.6-1-1-2.2-1-3.4 0-3.6 2.9-6.5 6.5-6.5 1.7 0 3.4.7 4.6 1.9 1.2 1.2 1.9 2.8 1.9 4.6-.1 3.5-3 6.4-6.5 6.4zm3.5-4.8c-.2-.1-1.1-.6-1.3-.6-.2-.1-.3-.1-.4.1-.1.2-.5.6-.6.8-.1.1-.2.1-.4 0-.9-.5-1.7-.9-2.2-2-.2-.3 0-.4.1-.5.1-.1.2-.2.3-.4.1-.1.1-.3.2-.4.1-.1 0-.3 0-.4-.1-.2-.4-1.1-.6-1.5-.2-.4-.3-.3-.5-.3h-.4c-.2 0-.4.1-.6.3-.8.9-.8 2.1.2 3.3.2.3 2 3.1 4.8 4.2.5.2.9.3 1.3.5.5.2 1 .1 1.4.1.4-.1 1.2-.5 1.4-1 .2-.5.2-.9.1-1 0-.1-.2-.2-.4-.3z" fill="currentColor"/>
            </svg>
            <div className="vt-profile-channel-content">
              <span className="vt-profile-channel-name">Support Team</span>
              <span className="vt-profile-channel-desc">Direct assistance</span>
            </div>
          </button>
        </div>
        
        <div className="vt-profile-support-info">
          <div className="vt-profile-support-hours">
            <div className="vt-profile-support-hours-header">
              <div className="vt-profile-hours-icon">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" strokeWidth="2"/>
                  <polyline points="12,6 12,12 16,14" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Support Hours</h3>
            </div>
            <ul className="vt-profile-hours-list">
              <li><span>Monday - Friday:</span> <span>8:00 AM - 8:00 PM</span></li>
              <li><span>Saturday:</span> <span>9:00 AM - 5:00 PM</span></li>
              <li><span>Sunday:</span> <span>Closed</span></li>
            </ul>
          </div>
          
          <div className="vt-profile-support-note">
            <div className="vt-profile-note-icon">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
              </svg>
            </div>
            <p>
              For urgent inquiries outside business hours, please use our Telegram channel,
              and a team member will respond as soon as possible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderChangePassword = () => (
    <div className="vt-profile-view-container">
      <ChangePassword token={token} />
    </div>
  );

  const renderAboutUs = () => (
    <div className="vt-profile-view-container">
      <div className="vt-profile-content-card vt-profile-about-card">
        <div className="vt-profile-about-header">
          <div className="vt-profile-company-emblem">
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="vt-profile-company-headline">
            <h1 className="vt-profile-company-name">VisionTech2030</h1>
            <p className="vt-profile-company-slogan">Innovating the Future of Investment</p>
          </div>
        </div>
        
        <div className="vt-profile-about-content">
          <div className="vt-profile-about-section">
            <h2>About Us</h2>
            <p>
              VisionTech2030 has been at the forefront of transforming the investment landscape for the past three years, 
              bringing cutting-edge opportunities in Tesla product investments to our community. Our company's success is 
              built on a foundation of trust, innovation, and a commitment to delivering high returns to our investors by 
              capitalizing on the electric vehicle (EV) and sustainable energy revolution led by Tesla.
            </p>
          </div>
          
          <div className="vt-profile-stats-container">
            <div className="vt-profile-stat-item">
              <div className="vt-profile-stat-icon">
                <Award size={24} />
              </div>
              <div className="vt-profile-stat-value">3+</div>
              <div className="vt-profile-stat-label">Years Market Leadership</div>
            </div>
            
            <div className="vt-profile-stat-item">
              <div className="vt-profile-stat-icon">
                <Lock size={24} />
              </div>
              <div className="vt-profile-stat-value">100%</div>
              <div className="vt-profile-stat-label">Tesla Focus</div>
            </div>
            
            <div className="vt-profile-stat-item">
              <div className="vt-profile-stat-icon">
                <FaChartLine size={24} />
              </div>
              <div className="vt-profile-stat-value">24/7</div>
              <div className="vt-profile-stat-label">Market Monitoring</div>
            </div>
          </div>
          
          <div className="vt-profile-about-section">
            <h2>Our Mission</h2>
            <p>
              Our mission is to provide forward-thinking investors with access to groundbreaking investment opportunities 
              in Tesla's range of innovative products. By focusing on electric vehicles, energy solutions, and future 
              technologies, we aim to create long-term value for our stakeholders while contributing to a sustainable, 
              cleaner, and smarter world.
            </p>
          </div>
          
          <div className="vt-profile-about-section">
            <h2>Innovation & Future Tech</h2>
            <p>
              At the core of VisionTech2030 lies a passion for innovation. Tesla's constant push toward pioneering 
              technology—ranging from electric vehicles to autonomous driving and energy solutions—is the future of 
              the tech industry, and we are proud to be a part of this evolution.
            </p>
          </div>
          
          <div className="vt-profile-join-cta">
            <h3>Join VisionTech2030 Today</h3>
            <p>Be a part of the future of investment!</p>
            <button className="vt-profile-cta-button">
              Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTermsOfService = () => (
    <div className="vt-profile-view-container">
      <div className="vt-profile-content-card vt-profile-terms-card">
        <div className="vt-profile-terms-header">
          <div className="vt-profile-terms-icon">
            <FileText size={28} />
          </div>
          <h1 className="vt-profile-terms-title">Terms of Use</h1>
        </div>
        
        <div className="vt-profile-terms-list">
          <div className="vt-profile-terms-item">
            <h2>1. ACCEPTANCE OF TERMS</h2>
            <p>
              By accessing or using the VisionTech2030 app, you agree to comply with these Terms of Use and all applicable laws and regulations. If you do not agree with any part of these terms, please refrain from using the app.
            </p>
          </div>
          
          <div className="vt-profile-terms-item">
            <h2>2. INVESTMENT RISKS</h2>
            <p>
              Investing in Tesla products and related technologies involves inherent risks. Past performance is not indicative of future results. By using this app, you acknowledge and accept that investing may result in the loss of some or all of your investment.
            </p>
          </div>
          
          <div className="vt-profile-terms-item">
            <h2>3. USER RESPONSIBILITIES</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for managing access to your device. You agree to accept responsibility for all activities that occur under your account.
            </p>
          </div>
          
          <div className="vt-profile-terms-item">
            <h2>4. PROHIBITED ACTIVITIES</h2>
            <p>
              When using our app, you agree not to engage in activities that include, but are not limited to:
            </p>
            <ul className="vt-profile-terms-bullets">
              <li>Violating any applicable laws or regulations</li>
              <li>Infringing on the intellectual property rights of others</li>
              <li>Transmitting or distributing harmful content</li>
              <li>Interfering with the operation or security of the app</li>
              <li>Engaging in fraudulent activities</li>
            </ul>
          </div>
          
          <div className="vt-profile-terms-item">
            <h2>5. PRIVACY AND DATA COLLECTION</h2>
            <p>
              Your privacy is important to us. By using this app, you agree to the collection, use, and sharing of your information as described in our Privacy Policy.
            </p>
          </div>
          
          <div className="vt-profile-terms-item">
            <h2>6. NO FINANCIAL ADVICE</h2>
            <p>
              The content provided on the VisionTech2030 app is for informational purposes only and should not be considered financial, investment, or legal advice.
            </p>
          </div>
          
          <div className="vt-profile-terms-item">
            <h2>7. CONTACT INFORMATION</h2>
            <p>
              If you have any questions or concerns about these Terms of Use, please contact us through our official channels listed on our website.
            </p>
          </div>
        </div>
        
        <div className="vt-profile-terms-footer">
          <p>Last updated: March 10, 2025</p>
        </div>
      </div>
    </div>
  );

  const renderCashoutView = () => (
    <div className="vt-profile-view-container">
      <CashoutView 
        setCurrentView={setCurrentView}
        userDetails={userDetails}
        token={token}
        calculateTotalBalance={calculateTotalBalance}
        setToastNotification={setToastNotification}
      />
    </div>
  );

  const renderView = () => {
    switch (currentView) {
      case 'deposit':
        return renderDepositRecord();
      case 'withdrawal':
        return renderWithdrawalRecord();
      case 'customer':
        return renderCustomerService();
      case 'password':
        return renderChangePassword();
      case 'about':
        return renderAboutUs();
      case 'terms':
        return renderTermsOfService();
      case 'wallet':
        return renderCashoutView();
      default:
        return renderMainView();
    }
  };

  return (
    <div className="vt-profile-container">
      {currentView !== 'main' && (
        <button className="vt-profile-back-button" onClick={() => setCurrentView('main')}>
          <FaArrowLeft /> Back
        </button>
      )}
      {renderView()}
      <BottomNavigation />
      <WhatsAppFloat />
      
      <style jsx>{`
        .vt-profile-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #0a1128 0%, #1a2456 100%);
          color: #fff;
          padding-bottom: 80px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          position: relative;
          overflow: hidden;
        }
        
        .vt-profile-background-decoration {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          overflow: hidden;
          height: 100%;
          z-index: 0;
          pointer-events: none;
        }
        
        .vt-profile-bg-circle {
          position: absolute;
          border-radius: 50%;
          opacity: 0.05;
        }
        
        .vt-profile-bg-circle-1 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, #4f46e5 0%, transparent 70%);
          top: -300px;
          right: -200px;
        }
        
        .vt-profile-bg-circle-2 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, #7e3af2 0%, transparent 70%);
          bottom: -100px;
          left: -100px;
        }
        
        /* Back Button */
        .vt-profile-back-button {
          background: rgba(255, 255, 255, 0.06);
          border: none;
          border-radius: 8px;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          margin: 16px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          backdrop-filter: blur(10px);
          z-index: 10;
          position: relative;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .vt-profile-back-button:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }
        
        /* Main View Styles */
        .vt-profile-main-view {
          padding: 20px 16px;
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        
        /* User Profile Section */
        .vt-profile-user-container {
          margin-bottom: 24px;
        }
        
        .vt-profile-user-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 10px 0;
        }
        
        .vt-profile-avatar-wrapper {
          position: relative;
        }
        
        .vt-profile-avatar-border {
          width: 74px;
          height: 74px;
          border-radius: 50%;
          background: conic-gradient(
            #4f46e5 0deg, 
            #7e3af2 120deg, 
            #a5b4fc 240deg, 
            #4f46e5 360deg
          );
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .vt-profile-avatar {
          width: 68px;
          height: 68px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1e293b, #334155);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          border: 2px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-profile-avatar-icon {
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        }
        
        .vt-profile-verified-badge {
          position: absolute;
          bottom: 0;
          right: 0;
          background: linear-gradient(135deg, #10b981, #059669);
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          border: 2px solid #0a1128;
        }
        
        .vt-profile-user-info {
          flex: 1;
        }
        
        .vt-profile-name {
          font-size: 22px;
          font-weight: 600;
          margin: 0 0 4px;
          color: #fff;
          line-height: 1.3;
        }
        
        .vt-profile-mobile {
          color: rgba(255, 255, 255, 0.7);
          font-size: 14px;
          margin: 0;
          line-height: 1.3;
        }
        
        /* Financial Summary */
        .vt-profile-financial-summary {
          margin-bottom: 24px;
        }
        
        .vt-profile-summary-card-inner {
          position: relative;
          z-index: 1;
          padding: 20px;
        }
        
        .vt-profile-summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        
        .vt-profile-summary-title {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
          color: rgba(255, 255, 255, 0.7);
        }
        
        .vt-profile-summary-action {
          background: rgba(79, 70, 229, 0.2);
          border: none;
          color: #a5b4fc;
          padding: 5px 10px;
          border-radius: 8px;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-profile-summary-action:hover {
          background: rgba(79, 70, 229, 0.3);
        }
        
        .vt-profile-balance-amount {
          display: flex;
          align-items: baseline;
          margin-bottom: 20px;
        }
        
        .vt-profile-currency {
          font-size: 18px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.8);
          margin-right: 8px;
        }
        
        .vt-profile-amount {
          font-size: 32px;
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.5px;
        }
        
        .vt-profile-quick-actions {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        
        .vt-profile-quick-action-btn {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .vt-profile-quick-action-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }
        
        .vt-profile-action-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .vt-profile-action-icon.deposit {
          background: rgba(16, 185, 129, 0.2);
          color: #34d399;
        }
        
        .vt-profile-action-icon.withdraw {
          background: rgba(99, 102, 241, 0.2);
          color: #818cf8;
        }
        
        /* Menu Section */
        .vt-profile-menu-container {
          margin-bottom: 30px;
        }
        
        .vt-profile-section-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 16px;
          color: rgba(255, 255, 255, 0.9);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .vt-profile-section-icon {
          opacity: 0.7;
        }
        
        .vt-profile-menu {
          display: grid;
          gap: 12px;
        }
        
        .vt-profile-menu-item {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 14px;
          overflow: hidden;
          transition: all 0.2s ease;
          border: 1px solid rgba(255, 255, 255, 0.06);
          display: flex;
          cursor: pointer;
        }
        
        .vt-profile-menu-item:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateY(-2px);
          border-color: rgba(255, 255, 255, 0.1);
        }
        
        .vt-profile-menu-icon-wrapper {
          padding: 16px 0 16px 16px;
        }
        
        .vt-profile-menu-icon {
          width: 42px;
          height: 42px;
          background: rgba(79, 70, 229, 0.1);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a5b4fc;
        }
        
        .vt-profile-menu-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
        }
        
        .vt-profile-menu-info {
          display: flex;
          flex-direction: column;
        }
        
        .vt-profile-menu-title {
          font-size: 15px;
          font-weight: 500;
          color: #fff;
          margin-bottom: 4px;
        }
        
        .vt-profile-menu-description {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
        }
        
        .vt-profile-menu-arrow {
          color: rgba(255, 255, 255, 0.3);
        }
        
        .vt-profile-menu-item:hover .vt-profile-menu-arrow {
          color: rgba(255, 255, 255, 0.6);
          transform: translateX(2px);
          transition: all 0.2s ease;
        }
        
        /* Logout Button */
        .vt-profile-logout-link {
          text-decoration: none;
          display: block;
          margin-bottom: 30px;
        }
        
        .vt-profile-logout-button {
          width: 100%;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
          color: #f87171;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px;
          border-radius: 14px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .vt-profile-logout-button:hover {
          background: rgba(239, 68, 68, 0.15);
          transform: translateY(-2px);
        }
        
        .vt-profile-logout-icon {
          font-size: 16px;
        }
        
        /* Footer */
        .vt-profile-footer {
          text-align: center;
          color: rgba(255, 255, 255, 0.4);
          font-size: 12px;
        }
        
        .vt-profile-footer-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 10px;
          color: rgba(255, 255, 255, 0.6);
        }
        
        .vt-profile-footer-copyright,
        .vt-profile-footer-version {
          margin: 4px 0;
        }
        
        /* View Container */
        .vt-profile-view-container {
          padding: 16px;
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        
        .vt-profile-content-card {
          background: rgba(16, 24, 64, 0.4);
          border-radius: 16px;
          padding: 24px;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-profile-view-header {
          margin-bottom: 20px;
        }
        
        .vt-profile-view-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #fff;
        }
        
        .vt-profile-view-subtitle {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
        }
        
        .vt-profile-subtitle-icon {
          color: #a5b4fc;
        }
        
        .vt-profile-view-description {
          color: rgba(255, 255, 255, 0.7);
          font-size: 15px;
          line-height: 1.6;
          margin-bottom: 24px;
        }
        
        /* Support View */
        .vt-profile-support-channels {
          display: grid;
          gap: 16px;
          margin-bottom: 30px;
        }
        
        .vt-profile-support-channel {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          border-radius: 14px;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }
        
        .vt-profile-support-channel.telegram {
          background: linear-gradient(to right, rgba(42, 171, 238, 0.2), rgba(34, 158, 217, 0.1));
          color: #2AABEE;
          border: 1px solid rgba(42, 171, 238, 0.2);
        }
        
        .vt-profile-support-channel.whatsapp {
          background: linear-gradient(to right, rgba(37, 211, 102, 0.2), rgba(18, 140, 126, 0.1));
          color: #25D366;
          border: 1px solid rgba(37, 211, 102, 0.2);
        }
        
        .vt-profile-support-channel:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
        }
        
        .vt-profile-channel-icon {
          width: 40px;
          height: 40px;
          flex-shrink: 0;
        }
        
        .vt-profile-channel-content {
          flex: 1;
        }
        
        .vt-profile-channel-name {
          display: block;
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 4px;
        }
        
        .vt-profile-channel-desc {
          display: block;
          font-size: 13px;
          opacity: 0.8;
        }
        
        .vt-profile-support-info {
          display: grid;
          gap: 20px;
        }
        
        .vt-profile-support-hours {
          background: rgba(255, 255, 255, 0.04);
          border-radius: 14px;
          padding: 16px;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .vt-profile-support-hours-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }
        
        .vt-profile-hours-icon {
          color: #a5b4fc;
        }
        
        .vt-profile-support-hours h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .vt-profile-hours-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .vt-profile-hours-list li {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }
        
        .vt-profile-hours-list li:last-child {
          border-bottom: none;
        }
        
        .vt-profile-support-note {
          display: flex;
          gap: 12px;
          background: rgba(79, 70, 229, 0.1);
          border-radius: 12px;
          padding: 16px;
          border-left: 3px solid #4f46e5;
        }
        
        .vt-profile-note-icon {
          color: #a5b4fc;
          flex-shrink: 0;
        }
        
        .vt-profile-support-note p {
          margin: 0;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.5;
        }
        
        /* About View */
        .vt-profile-about-card {
          padding: 0;
          overflow: hidden;
        }
        
        .vt-profile-about-header {
          background: linear-gradient(135deg, rgba(79, 70, 229, 0.3), rgba(126, 58, 242, 0.3));
          padding: 30px 24px;
          text-align: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-profile-company-emblem {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: #fff;
          box-shadow: 0 6px 15px rgba(79, 70, 229, 0.3);
        }
        
        .vt-profile-company-headline {
          margin-bottom: 10px;
        }
        
        .vt-profile-company-name {
          font-size: 26px;
          font-weight: 700;
          margin: 0 0 4px;
          color: #fff;
        }
        
        .vt-profile-company-slogan {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }
        
        .vt-profile-about-content {
          padding: 24px;
        }
        
        .vt-profile-about-section {
          margin-bottom: 24px;
        }
        
        .vt-profile-about-section h2 {
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 12px;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .vt-profile-about-section p {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }
        
        .vt-profile-stats-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin: 30px 0;
        }
        
        .vt-profile-stat-item {
          background: rgba(255, 255, 255, 0.04);
          border-radius: 14px;
          padding: 16px;
          text-align: center;
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s ease;
        }
        
        .vt-profile-stat-item:hover {
          transform: translateY(-3px);
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(79, 70, 229, 0.3);
        }
        
        .vt-profile-stat-icon {
          margin-bottom: 10px;
          color: #a5b4fc;
        }
        
        .vt-profile-stat-value {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 8px;
          background: linear-gradient(90deg, #a5b4fc, #6366f1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .vt-profile-stat-label {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.4;
        }
        
        .vt-profile-join-cta {
          background: linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(126, 58, 242, 0.1));
          border-radius: 14px;
          padding: 20px;
          text-align: center;
          border: 1px solid rgba(79, 70, 229, 0.2);
        }
        
        .vt-profile-join-cta h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 8px;
          color: #fff;
        }
        
        .vt-profile-join-cta p {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0 0 16px;
        }
        
        .vt-profile-cta-button {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: #fff;
          border: none;
          padding: 12px 24px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-profile-cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(79, 70, 229, 0.4);
        }
        
        /* Terms View */
        .vt-profile-terms-card {
          padding: 0;
        }
        
        .vt-profile-terms-header {
          background: linear-gradient(135deg, rgba(79, 70, 229, 0.2), rgba(126, 58, 242, 0.2));
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-profile-terms-icon {
          color: #a5b4fc;
        }
        
        .vt-profile-terms-title {
          font-size: 22px;
          font-weight: 700;
          margin: 0;
          color: #fff;
        }
        
        .vt-profile-terms-list {
          padding: 24px;
        }
        
        .vt-profile-terms-item {
          margin-bottom: 24px;
        }
        
        .vt-profile-terms-item h2 {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 12px;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .vt-profile-terms-item p {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.7);
          margin: 0 0 12px;
        }
        
        .vt-profile-terms-bullets {
          padding-left: 18px;
          margin: 12px 0;
        }
        
        .vt-profile-terms-bullets li {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 8px;
          line-height: 1.5;
        }
        
        .vt-profile-terms-footer {
          background: rgba(255, 255, 255, 0.03);
          padding: 16px 24px;
          text-align: center;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .vt-profile-terms-footer p {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          margin: 0;
        }
        
        /* Responsive Styles */
        @media (min-width: 768px) {
          .vt-profile-support-channels {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        @media (max-width: 640px) {
          .vt-profile-stats-container {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .vt-profile-terms-header {
            flex-direction: column;
            text-align: center;
            gap: 10px;
          }
          
          .vt-profile-about-header {
            padding: 24px 16px;
          }
          
          .vt-profile-about-content {
            padding: 20px 16px;
          }
        }
        
        @media (max-width: 480px) {
          .vt-profile-user-header {
            flex-direction: column;
            text-align: center;
          }
          
          .vt-profile-avatar-wrapper {
            margin: 0 auto;
          }
        }

          }
      `}</style>
    </div>
  );
};

export default Me;
          
